import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@ui-elements/Button';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { ROUTES } from '~/constants/routes';
import { PALETTE, FONT_SIZES, TYPOGRAPHY, ADMIN_PALETTE } from '~/assets/styles/base';
import { registerAdmin } from '~/apiTs';
import { validationInitialized } from '~/features/validation/validationSlice';
import { GMOASK_STATUS } from '~/constants/api';

const StyledBox = styled.div`
  background: ${PALETTE.white};
  width: 720px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 55px auto;
  padding: 55px 50px;
  a {
    text-decoration: none;
  }
`;

const InputWrapper = styled.div`
  width: 620px;
  text-align: left;
  border-bottom: 1px solid ${PALETTE.border.frame};
  margin: 20px 0;
  font-size: ${TYPOGRAPHY.body3};
`;

const InputTitle = styled.div`
  font-size: ${FONT_SIZES.body3};
  margin: 10px 0;
`;

const InputText = styled.div`
  font-size: ${FONT_SIZES.bodyDefault};
  margin: 10px 0;
  word-break: break-word;
`;
const InputTextP = styled.input`
  font-size: ${FONT_SIZES.bodyDefault};
  margin: 10px 0;
  background: ${PALETTE.white};
  border: none;
`;
const FlexWrap = styled.div`
  display: flex;
  padding: 0 100px;
  justify-content: space-evenly;
`;

const Title = styled(Typography)`
  text-align: center;
  font-size: ${TYPOGRAPHY.title4};
  color: ${ADMIN_PALETTE.primary.main};
  margin-bottom: 30px;
`;

const StyledAlert = styled.div`
  border: 1px solid ${PALETTE.error.main};
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 12px 16px;
  margin-top: -10px;
  margin-bottom: 26px;
  position: relative;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  svg {
    font-size: 1.8rem;
    margin-right: 2px;
  }
`;

function AdminSignupConfirm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const admin = useSelector((state) => state.admin);
  const navigate = useNavigate();
  const validation = useSelector((state) => state.validation);

  useEffect(() => {
    dispatch(validationInitialized('signup'));
    // signupページを介さずに直接confirmページのURL入力した場合は登録画面に飛ばす
    if (admin.name === '') {
      navigate(ROUTES.ADMIN.SIGNUP);
    }
  }, []);

  const handleClickSubmit = async () => {
    const res = await dispatch(
      registerAdmin({
        name: admin.name,
        email: admin.email,
        password: admin.password,
      })
    );
    if (res.payload.status === GMOASK_STATUS.OK) {
      navigate(ROUTES.ADMIN.SIGNUP_COMPLETED);
    }
  };

  useEffect(() => {
    if (validation.signup.errors.invalid) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [validation.signup.errors.invalid]);

  return (
    <StyledBox>
      <Title>{t('registration.proceedNext')}</Title>
      {validation.signup.errors.invalid && (
        <StyledAlert severity="error" color="error">
          <StyledTypography variant="body3">
            <WarningRoundedIcon />
            <span data-testid="Admin-Signup-Response-Error-Message">
              {validation.signup.errors.message}
            </span>
          </StyledTypography>
        </StyledAlert>
      )}
      <InputWrapper>
        <InputTitle data-testid="pages-AdminSignupConfirm-label-email">
          {t('registration.email')}
        </InputTitle>
        <InputText data-testid="pages-AdminSignupConfirm-input-email">{admin.email}</InputText>
      </InputWrapper>
      <InputWrapper>
        <InputTitle data-testid="pages-AdminSignupConfirm-label-password">
          {t('registration.password')}
        </InputTitle>
        <InputTextP
          data-testid="pages-AdminSignupConfirm-input-password"
          type="password"
          value={admin.password}
          disabled
        />
      </InputWrapper>
      <InputWrapper>
        <InputTitle data-testid="pages-AdminSignupConfirm-label-name">
          {t('registration.name')}
        </InputTitle>
        <InputText data-testid="pages-AdminSignupConfirm-input-name">{admin.name}</InputText>
      </InputWrapper>
      <FlexWrap>
        <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
          <Button
            variant="outlined"
            color="secondary"
            colorType="gray"
            handleClick={() => {
              navigate(ROUTES.ADMIN.SIGNUP);
            }}
            data-testid="pages-AdminSignupConfirm-backbtn"
            sx={{ width: 148 }}
          >
            {t('appCommon.back')}
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
          <Button
            variant="contained"
            data-testid="pages-AdminSignupConfirm-submitbtn"
            onClick={handleClickSubmit}
            sx={{ width: 148 }}
          >
            {t('appCommon.submit')}
          </Button>
        </Box>
      </FlexWrap>
    </StyledBox>
  );
}

export default AdminSignupConfirm;
